import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation, useMatch } from "react-router-dom";
import { Button, Grid, Group, Title, Modal, Stack, Text, useMantineTheme, Drawer, Collapse } from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { IconPlus } from "@tabler/icons-react";

import { CategoriesList } from "../../Components/Categories/CategoryList";
import { CategoryDetailView } from "../../Components/Categories/CategoryDetailView";
import CategoryEditView from "../../Components/Categories/CategoryEditView";
import { ListCategoryDto, MutableCategoryDto } from "../../models/categories.models";
import { useGetCategoryByIdQuery, useGetCategoriesQuery, useUpdateCategoryMutation, useDeleteCategoryMutation, useCreateCategoryMutation } from "../../api/category.api";
import { AzureADRoles } from "../../Auth/auth-roles";
import { RoleTemplate } from "../../Components/RoleTemplate";
import { useSearch } from "../../functions/SearchBox.hook";

const CategoriesPage = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
	const params = useParams<{ categoryId?: string }>();
	const isEditing = useMatch("/categories/:categoryId/edit") !== null;
	const isNew = params.categoryId === "new";
	const categoryIdFromParams = params.categoryId || null;
	const { SearchBox, visible: searchVisible } = useSearch();

	const { data: categories, isPending: isLoadingCategories } = useGetCategoriesQuery();
	const [selectedCategory, setSelectedCategory] = useState<ListCategoryDto | null>(null);
	const { data: category, isLoading } = useGetCategoryByIdQuery(selectedCategory?.CategoryId || categoryIdFromParams || null);

	const [discardChangesModalOpened, { open: openDiscardChangesModal, close: closeDiscardChangesModal }] = useDisclosure(false);
	const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
	const [detailViewDrawerOpened, { open: openDetailViewDrawer, close: closeDetailViewDrawer }] = useDisclosure(false);
	const [isChanged, setIsChanged] = useState(false);
	const [requestedCategoryId, setRequestedCategoryId] = useState<string | null>(null);

	const { mutate: createCategory, isPending: isCreating } = useCreateCategoryMutation();
	const { mutate: updateCategory, isPending: isUpdating } = useUpdateCategoryMutation();
	const { mutate: deleteCategory } = useDeleteCategoryMutation();

	const isSaving = isCreating || isUpdating;

	useEffect(() => {
		if (categoryIdFromParams) {
			const cat = categories?.find((c: ListCategoryDto) => c.CategoryId === categoryIdFromParams);
			if (cat) {
				setSelectedCategory?.(cat);
				if (isMobile) {
					openDetailViewDrawer();
				}
			}
		} else if (categories && categories.length > 0) {
			setSelectedCategory?.(categories[0]);
		}
	}, [categoryIdFromParams, categories, isMobile]);

	useEffect(() => {
		if (!categoryIdFromParams && isMobile) {
			closeDetailViewDrawer();
		}
	}, [location.pathname]);

	const handleEdit = () => {
		navigate(`/categories/${category?.CategoryId}/edit`);
	};

	const handleNew = () => {
		setIsChanged(false);
		setSelectedCategory(null);
		navigate(`/categories/new`);

		if (isMobile) {
			openDetailViewDrawer();
		}
	};

	const handleCategorySelect = (category: ListCategoryDto | null) => {
		if (!category) return;
		setRequestedCategoryId(category.CategoryId);
		if (isChanged && !discardChangesModalOpened) {
			openDiscardChangesModal();
			return;
		}
		setSelectedCategory(category);
		navigate(`/categories/${category.CategoryId}`);
		setIsChanged(false);

		if (isMobile) {
			openDetailViewDrawer();
		}
	};

	const handleSave = (category: MutableCategoryDto) => {
		if (isNew) {
			createCategory(category, {
				onSuccess: (response) => {
					setIsChanged(false);
					navigate(`/categories/${response.data.CategoryId}`, { replace: true });
				}
			});
		} else {
			updateCategory(category, {
				onSuccess: () => {
					
					setIsChanged(false);
					// setIsEditing(false);
					// history.replace(`/categories/${category?.CategoryId}`);
					navigate(-1);
				}
			});
		}
	};

	const handleDelete = () => {
		if (!category) return;

		closeDeleteModal();
		deleteCategory(category, {
			onSuccess: () => {
				setSelectedCategory(null);
				navigate(`/categories`, { replace: true });
			}
		});
	}

	const handleClose = () => {
		if (isChanged && !discardChangesModalOpened) {
			openDiscardChangesModal();
		} else {
			closeDiscardChangesModal();
			setIsChanged(false);
			navigate(-1);

			if (isMobile && isNew) {
				closeDetailViewDrawer();
			}
		}
	};

	const handleChanged = (changed: boolean) => {
		setIsChanged(changed);
	};

	const handleDiscardChanges = () => {
		closeDiscardChangesModal();
		setIsChanged(false);

		if (isMobile && isNew) {
			closeDetailViewDrawer();
		}

		if (requestedCategoryId && requestedCategoryId !== category?.CategoryId) {
			navigate(`/categories/${requestedCategoryId}`);
			setRequestedCategoryId(null);
		} else {
			navigate(-1);
		}
	};

	const handleCancelDiscard = () => {
		closeDiscardChangesModal();
	};

	const handleDetailViewDrawerClose = () => {
		navigate(-1);
	}

	return <>
		<Modal opened={discardChangesModalOpened} onClose={handleCancelDiscard} title={<Text fz="xl" fw="bold">Discard unsaved changes?</Text>} radius="lg" zIndex={1000}>
			<Stack>
				<Text>If you leave now, you’ll lose unsaved changes to this category.</Text>
				<Group justify="right">
					<Button variant="outline" onClick={handleCancelDiscard} radius="lg">Cancel</Button>
					<Button variant="filled" onClick={handleDiscardChanges} radius="lg">Discard Changes</Button>
				</Group>
			</Stack>
		</Modal>

		<Modal opened={deleteModalOpened} onClose={closeDeleteModal} title={<Text fz="xl" fw="bold">Delete Category</Text>} radius="lg" zIndex={1000}>
			<Stack>
				<Text>Are you sure you want to delete the category:</Text>
				<Text fw="bold">{category?.Title}</Text>
				<Group justify="right">
					<Button variant="outline" onClick={closeDeleteModal} radius="lg">Cancel</Button>
					<Button color="red" onClick={handleDelete} radius="lg">Delete</Button>
				</Group>
			</Stack>
		</Modal>

		<Drawer opened={detailViewDrawerOpened} onClose={handleDetailViewDrawerClose} withCloseButton={false} padding={0} size="xl" position="right">
			{isEditing || isNew ? (
				<CategoryEditView category={!isNew ? category : null} onSave={handleSave} onClose={handleClose} isSaving={isSaving} onChanged={handleChanged} />
			) : (
				<CategoryDetailView category={category} isLoading={isLoading} onEdit={handleEdit} onClose={handleDetailViewDrawerClose} onDelete={openDeleteModal} />
			)}
		</Drawer>

		<Collapse in={searchVisible} mb="md" hiddenFrom="sm">
			{SearchBox}	
		</Collapse>

		<Group justify="space-between">
			<Title order={2}>Categories</Title>
			<RoleTemplate role={AzureADRoles.Admin}>
				<Button radius="xl" leftSection={<IconPlus />} onClick={handleNew}>New Category</Button>
			</RoleTemplate>
		</Group>

		<Grid mt="lg" gutter="md">
			<Grid.Col span={{ base: 12, xs: ((!categories || categories.length === 0) && !isNew && !isEditing) ? 12 : 5 }}>
				<CategoriesList categories={categories} isLoading={isLoadingCategories} selectedCategory={selectedCategory} setSelectedCategory={handleCategorySelect} />
			</Grid.Col>
			<Grid.Col span={{ base: 12, xs: 7, md: 7, lg: 7 }} visibleFrom="xs">
				{isEditing || isNew ? (
					<CategoryEditView category={!isNew ? category : null} onSave={handleSave} onClose={handleClose} isSaving={isSaving} onChanged={handleChanged} />
				) : categories && categories.length > 0 ? (
					<CategoryDetailView category={category} isLoading={isLoading} onEdit={handleEdit} onDelete={openDeleteModal} />
				) : <></>}
			</Grid.Col>
		</Grid>
	</>;
};

export default CategoriesPage;